import { useCallback, useEffect, useRef, useState } from "react";

export function useDebounce<T>(value: T, delayMs: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedValue(value), delayMs);
		return () => clearTimeout(handle);
	}, [value, delayMs]);

	return debouncedValue;
}

export function useDebounceCallback<T extends (...args: unknown[]) => void>(callback: T, delayMs = 500) {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const debouncedFn = useCallback(
		(...args: Parameters<T>) => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				callback(...args);
			}, delayMs);
		},
		[delayMs]
	);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	return debouncedFn;
}